import {
  Button as FountainButton,
  Input,
} from '@fountain/fountain-ui-components';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from '@material-ui/core';
import { Icon, XOutlineIcon } from '@fountain/ripple/icon';
import React, { useEffect, useState, VFC } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import globalMessages from 'shared/global/messages';
import { iconLabels } from 'shared/iconLabels';

import { messages } from './messages';
import { useStyles } from './styles';

const MAX_DESCRIPTION_LENGTH = 500;

export interface RenameWorkflowModalProps {
  onWorkflowUpdate: (workflowName: string, workflowDescription: string) => void;
  renameModalVisible: boolean;
  setRenameModalVisible: (modalVisible: boolean) => void;
  workflowTitle: string;
  workflowDescription?: string;
}

export const RenameWorkflowModal: VFC<RenameWorkflowModalProps> = ({
  onWorkflowUpdate,
  renameModalVisible,
  setRenameModalVisible,
  workflowTitle,
  workflowDescription = '',
}) => {
  const intl = useIntl();
  const styles = useStyles();
  const [workflowName, setWorkflowName] = useState(workflowTitle);
  const [description, setDescription] = useState(workflowDescription);

  // Reset form when modal opens or props change
  useEffect(() => {
    if (renameModalVisible) {
      setWorkflowName(workflowTitle);
      setDescription(workflowDescription);
    }
  }, [renameModalVisible, workflowTitle, workflowDescription]);

  const toggleRenameModal = () => {
    setRenameModalVisible(!renameModalVisible);
  };

  const handleDescriptionChange = (
    event: React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    const { value } = event.target;
    if (value.length <= MAX_DESCRIPTION_LENGTH) {
      setDescription(value);
    }
  };

  const onSubmit = () => {
    onWorkflowUpdate(workflowName, description);
    setRenameModalVisible(false);
  };

  return (
    <Dialog open={renameModalVisible} onClose={toggleRenameModal} fullWidth>
      <DialogTitle className={styles.dialogHeader} disableTypography>
        <Typography variant="h3" className={styles.dialogTitle}>
          <FormattedMessage {...messages.renameWorkflow} />
        </Typography>
        <IconButton
          aria-label={intl.formatMessage(iconLabels.close)}
          onClick={toggleRenameModal}
        >
          <Icon icon={XOutlineIcon} size="lg" color="inherit" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" className={styles.dialogBody}>
          <FormattedMessage {...messages.editWorkflowDescription} />
        </Typography>
        <br />
        <Input
          value={workflowName}
          onChange={event => setWorkflowName(event.target.value)}
          label={intl.formatMessage(messages.renameLabel)}
          variant="outlined"
        />
        <br />
        <br />
        <TextField
          value={description}
          onChange={handleDescriptionChange}
          label={intl.formatMessage(messages.descriptionLabel)}
          variant="outlined"
          fullWidth
          multiline
          rows={3}
          rowsMax={5}
          helperText={intl.formatMessage(messages.descriptionHelperText, {
            count: description.length,
          })}
          inputProps={{
            maxLength: MAX_DESCRIPTION_LENGTH,
          }}
        />
      </DialogContent>
      <DialogActions className={styles.footer}>
        <FountainButton
          onClick={toggleRenameModal}
          className={styles.cancelButton}
          size="small"
          type="secondary"
        >
          <FormattedMessage {...globalMessages.cancel} />
        </FountainButton>
        <FountainButton
          onClick={onSubmit}
          color="primary"
          size="small"
          className={styles.renameButton}
        >
          <FormattedMessage {...globalMessages.save} />
        </FountainButton>
      </DialogActions>
    </Dialog>
  );
};
